/* Live Room — the bidder's seat at a live/hybrid auction (Phase 4).
   Stream on top, one-tap bid at the auctioneer's ask, fair-warning tension,
   celebration on a win. Spectators (unregistered) watch everything live. */
import { motion, AnimatePresence } from "framer-motion";
import { Eye, Gavel, Radio, SearchX } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { BackLink } from "@/components/BackLink";
import { eventBase, useBrandedRedirect } from "@/lib/microsite";
import { Celebration } from "@/components/Celebration";
import { EmptyState } from "@/components/EmptyState";
import { RegisterToBidDialog } from "@/components/bidding/RegisterToBidDialog";
import { StreamEmbed } from "@/components/live/StreamEmbed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { useEventSocket, useServerCountdown } from "@/hooks/useSockets";
import { api, BACKEND_URL, formatApiErrorDetail } from "@/lib/api";
import { formatCurrency, localized } from "@/lib/intl";
import { usePageMeta } from "@/lib/seo";
import { brandStyle } from "@/lib/theme";

const coverSrc = (url) => (url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : null);

/* Dock inferior persistente SOLO móvil para live/hybrid — capa de presentación
   sobre el MISMO estado de sesión (live_state) y la MISMA acción placeBid de
   LiveRoom (cero lógica de puja nueva). Aparece solo con lote activo abierto. */
const LiveDock = ({ session, lot, horse, me, iLead, outbid, canBid, busy, fairWarning, currency, locale, user, onBid, onOpenRegister }) => {
  const { t } = useTranslation();
  const hasBids = (lot?.bid_count || 0) > 0;
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 lg:hidden" data-testid="live-dock">
      <div
        className={`bg-white/95 backdrop-blur-xl border-t rounded-t-3xl shadow-[0_-12px_40px_rgb(0,0,0,0.14)] ${
          fairWarning ? "border-[#E8A33D]/60" : "border-black/10"
        }`}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {fairWarning && (
          <div className="flex items-center justify-center gap-2 bg-[#E8A33D]/15 px-4 py-1.5 rounded-t-3xl" data-testid="live-dock-fair-warning">
            <Gavel className="w-3.5 h-3.5 text-[#B27A1E] animate-pulse" aria-hidden="true" />
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#8A5E14]">{t("live.fairWarningBanner")}</span>
          </div>
        )}
        <div className="flex items-center gap-3 px-5 py-3">
          <div className="flex flex-col min-w-0">
            <span
              className={`text-[10px] font-semibold uppercase tracking-[0.18em] inline-flex items-center gap-1.5 ${
                iLead ? "text-[#1D4D3B]" : outbid ? "text-[#B42318]" : "text-[#737373]"
              }`}
              data-testid="live-dock-state"
            >
              {(iLead || outbid) && (
                <span className={`w-1.5 h-1.5 rounded-full ${iLead ? "bg-[#1D4D3B]" : "bg-[#B42318] animate-pulse"}`} aria-hidden="true" />
              )}
              {iLead
                ? t("bid.dockLeading")
                : outbid
                  ? t("bid.dockOutbid")
                  : `${t("pub.lotN", { number: lot.lot_number })} · ${session.current_index + 1}/${session.total_lots}`}
            </span>
            <span className="text-sm font-semibold text-[#0A0A0A] truncate" data-testid="live-dock-lot">
              {horse?.name || t("pub.lotN", { number: lot.lot_number })}
              <span className="tabular-nums text-[#737373] font-medium" data-testid="live-dock-price">
                {" "}· {formatCurrency(hasBids ? lot.current_price_cents : lot.starting_price_cents, currency, locale)}
              </span>
            </span>
          </div>
          {iLead ? (
            <div className="ml-auto shrink-0 rounded-full bg-[#1D4D3B] text-white h-11 px-5 inline-flex items-center text-sm font-bold" data-testid="live-dock-you-lead">
              {t("live.youLead")} · #{me.paddle}
            </div>
          ) : canBid ? (
            <Button
              data-testid="live-dock-bid-button"
              onClick={onBid}
              disabled={busy}
              className={`ml-auto shrink-0 h-11 px-5 rounded-full bg-[#0A0A0A] text-white text-sm font-bold active:scale-[0.97] transition-[transform,background-color,box-shadow] duration-200 ${
                fairWarning ? "ring-2 ring-[#E8A33D]" : ""
              }`}
            >
              {t("live.bidNow", { amount: formatCurrency(session.current_ask_cents, currency, locale) })}
            </Button>
          ) : (
            <div className="ml-auto shrink-0 text-right" data-testid="live-dock-spectator">
              <p className="text-xs font-semibold tabular-nums text-[#0A0A0A]">
                {t("live.askLabel", { amount: session.current_ask_cents ? formatCurrency(session.current_ask_cents, currency, locale) : "—" })}
              </p>
              {!user ? (
                <Link to="/signin" data-testid="live-dock-signin-cta" className="text-[11px] font-medium text-[#1D4D3B] underline underline-offset-2">
                  {t("live.signInToBid")}
                </Link>
              ) : !me?.registered ? (
                <button type="button" data-testid="live-dock-register-cta" onClick={onOpenRegister} className="text-[11px] font-medium text-[#1D4D3B] underline underline-offset-2">
                  {t("live.registerToBid")}
                </button>
              ) : (
                <p className="text-[10px] text-[#737373]">{t("live.registrationPending")}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StartsIn = ({ target, offsetRef }) => {
  const { t } = useTranslation();
  const p = useServerCountdown(target, offsetRef);
  if (!target || p.total <= 0) return null;
  const seg = (v) => String(v).padStart(2, "0");
  return (
    <p className="text-3xl font-semibold tabular-nums tracking-tight text-[#0A0A0A]" data-testid="live-room-countdown" role="timer">
      {p.days > 0 ? `${p.days}${t("common.dShort")} ` : ""}
      {seg(p.hours)}:{seg(p.minutes)}:{seg(p.seconds)}
    </p>
  );
};

export default function LiveRoom() {
  const { slug: legacySlug, eventSlug } = useParams();
  const slug = eventSlug || legacySlug;
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { user } = useAuth();

  const [payload, setPayload] = useState(undefined); // /public/events/{slug}
  const [live, setLive] = useState(undefined); // /public/live/{slug}
  const [me, setMe] = useState(null); // /live/sessions/{id}/me
  const [busy, setBusy] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [celebrate, setCelebrate] = useState(0);
  const [won, setWon] = useState(false);
  const [flash, setFlash] = useState(false);
  const myPaddleRef = useRef(null);
  const everBidLotRef = useRef(null); // último lote donde YO pujé (para "Superado" en el dock)

  const loadLive = useCallback(
    () => api.get(`/public/live/${slug}`).then(({ data }) => setLive(data)).catch(() => setLive(null)),
    [slug]
  );

  useEffect(() => {
    api.get(`/public/events/${slug}`).then(({ data }) => setPayload(data)).catch(() => setPayload(false));
    loadLive();
  }, [slug, loadLive]);

  const sid = live?.session?.id;

  useEffect(() => {
    if (!sid || !user) return;
    api
      .get(`/live/sessions/${sid}/me`)
      .then(({ data }) => {
        setMe(data);
        myPaddleRef.current = data.paddle;
      })
      .catch(() => {});
  }, [sid, user]);

  const onSocketMessage = useCallback(
    (msg) => {
      if (msg.type !== "live_state") return;
      setLive((prev) => ({ ...(prev || {}), ...msg, event: prev?.event || msg.event }));
      if (msg.last_bid) {
        setFlash(true);
        setTimeout(() => setFlash(false), 700);
      }
      if (msg.lot && msg.session?.lot_state !== "sold") setWon(false);
      if (msg.celebrate && msg.lot?.winner_paddle != null && msg.lot.winner_paddle === myPaddleRef.current) {
        setWon(true);
        setCelebrate((c) => c + 1);
      }
    },
    []
  );
  const { connected, watchers, offsetRef } = useEventSocket(payload?.event?.id, onSocketMessage);

  // resync on reconnect
  const wasConnected = useRef(false);
  useEffect(() => {
    if (connected && wasConnected.current === false && live !== undefined) loadLive();
    wasConnected.current = connected;
  }, [connected, loadLive]); // eslint-disable-line react-hooks/exhaustive-deps

  usePageMeta({
    title: payload?.event ? `${t("live.roomTitle")} — ${localized(payload.event.title, locale)}` : undefined,
  });

  const placeBid = async () => {
    if (!sid || busy) return;
    setBusy(true);
    try {
      await api.post(`/live/sessions/${sid}/bid`, { ask_cents: live.session.current_ask_cents });
      everBidLotRef.current = live?.lot?.id ?? null;
      toast.success(t("live.bidAccepted"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail.code : null;
      if (code === "ask_changed" && detail.current_ask_cents) {
        toast(t("live.err_ask_changed"));
        loadLive();
      } else {
        toast.error(code ? t(`live.err_${code}`, { defaultValue: formatApiErrorDetail(detail) || t("auth.genericError") }) : t("auth.genericError"));
      }
    } finally {
      setBusy(false);
    }
  };

  // ---------- guards ----------
  useBrandedRedirect(payload?.organizer?.slug, payload?.event?.slug);
  if (payload === false) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24">
        <EmptyState icon={SearchX} title={t("pub.notFoundTitle")} description={t("pub.notFoundDesc")} data-testid="live-room-not-found" />
      </div>
    );
  }

  if (payload?.gated) return <Navigate to={eventBase(payload?.organizer?.slug, slug)} replace />;
  if (payload === undefined || live === undefined) {
    return (
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-10 flex flex-col gap-6" data-testid="live-room-skeleton">
        <Skeleton className="h-8 w-64 rounded-xl" />
        <Skeleton className="aspect-video rounded-2xl" />
        <Skeleton className="h-48 rounded-3xl" />
      </div>
    );
  }

  const session = live?.session;
  const lot = live?.lot;
  const horse = live?.horse;
  const currency = lot?.currency || "EUR";
  const liveAuction = payload?.auctions?.find((a) => ["live", "hybrid"].includes(a.type));
  const streamUrl = session?.streaming_url || payload?.event?.streaming_url;
  const lotState = session?.lot_state;
  const fairWarning = lotState === "fair_warning";
  const hasBids = (lot?.bid_count || 0) > 0;
  const iLead = Boolean(hasBids && session?.leading_source === "online" && me?.paddle != null && lot?.leading_paddle === me.paddle);
  const canBid = Boolean(user && me?.approved && session?.status === "live" && ["open", "fair_warning"].includes(lotState) && session?.current_ask_cents && !iLead);
  const spectator = !user || !me?.approved;
  // dock móvil: solo mientras hay un lote activo abierto a pujas
  const dockVisible = Boolean(session?.status === "live" && lot && ["open", "fair_warning"].includes(lotState));
  const outbid = Boolean(lot && everBidLotRef.current === lot.id && hasBids && !iLead);

  return (
    <div style={brandStyle(payload?.organizer)} data-testid="live-room-page">
      <Celebration trigger={celebrate} />
      <div className={`max-w-5xl mx-auto px-6 lg:px-12 py-8 lg:py-10 flex flex-col gap-6 ${dockVisible ? "pb-32 lg:pb-10" : ""}`}>
        {/* header row */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <BackLink fallback={eventBase(payload?.organizer?.slug, slug)} testid="live-room-back" label={t("bid.backToEvent")} />
            <h1 className="text-lg font-semibold tracking-tight text-[#0A0A0A] truncate hidden sm:block" data-testid="live-room-title">
              {localized(payload?.event?.title, locale)}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {session?.status === "live" && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#B42318] text-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em]" data-testid="live-room-live-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" aria-hidden="true" />
                {t("live.liveNow")}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 text-xs text-[#737373]" data-testid="live-room-watchers">
              <Eye className="w-3.5 h-3.5" aria-hidden="true" />
              {t("bid.watchers", { count: watchers })}
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-[#737373]" data-testid="live-room-connection">
              <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-[#1D4D3B]" : "bg-[#B42318] animate-pulse"}`} aria-hidden="true" />
              <span className="hidden sm:inline">{connected ? t("bid.connected") : t("bid.reconnecting")}</span>
            </span>
          </div>
        </div>

        {/* stream */}
        {streamUrl && <StreamEmbed url={streamUrl} testid="live-room-stream" />}

        {/* ---------- pre-session / scheduled ---------- */}
        {(!session || session.status === "scheduled") && (
          <div className="rounded-3xl border border-black/5 bg-[#F9F9F8] px-8 py-14 flex flex-col items-center gap-4 text-center" data-testid="live-room-waiting">
            <div className="w-14 h-14 rounded-full bg-[#1D4D3B]/10 flex items-center justify-center" aria-hidden="true">
              <Radio className="w-6 h-6 text-[#1D4D3B]" />
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-[#0A0A0A]">{t("live.doorsSoonTitle")}</h2>
            <p className="text-sm text-[#737373] max-w-md">{t("live.doorsSoonDesc")}</p>
            <StartsIn target={liveAuction?.starts_at} offsetRef={offsetRef} />
          </div>
        )}

        {/* ---------- live ---------- */}
        {session?.status === "live" && (
          <>
            {/* fair warning banner */}
            <AnimatePresence>
              {fairWarning && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="rounded-2xl bg-[#E8A33D]/15 border border-[#E8A33D]/50 px-6 py-4 flex items-center gap-3"
                  data-testid="live-room-fair-warning"
                  role="alert"
                >
                  <Gavel className="w-5 h-5 text-[#B27A1E] animate-pulse" aria-hidden="true" />
                  <p className="font-bold text-[#8A5E14] text-sm uppercase tracking-[0.12em]">{t("live.fairWarningBanner")}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {lot ? (
              <div
                className={`rounded-3xl border p-6 lg:p-8 transition-[border-color,box-shadow] duration-500 ${
                  fairWarning ? "border-[#E8A33D]/60 shadow-[0_0_0_4px_rgba(232,163,61,0.12)]" : "border-black/5"
                }`}
                data-testid="live-room-lot-card"
              >
                <div className="flex flex-col sm:flex-row gap-6">
                  {coverSrc(horse?.cover_url) && (
                    <img src={coverSrc(horse.cover_url)} alt={horse?.name || ""} className="w-full sm:w-44 h-44 rounded-2xl object-cover" />
                  )}
                  <div className="flex-1 min-w-0 flex flex-col gap-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#737373]" data-testid="live-room-lot-number">
                        {t("pub.lotN", { number: lot.lot_number })} · {session.current_index + 1}/{session.total_lots}
                      </p>
                      <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight text-[#0A0A0A] mt-1" data-testid="live-room-horse-name">
                        {horse?.name}
                      </h2>
                    </div>

                    <div className="flex items-end justify-between gap-4 flex-wrap">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#737373]">
                          {lotState === "sold" ? t("bid.finalPrice") : hasBids ? t("bid.currentBid") : t("bid.startingPrice")}
                        </p>
                        <p
                          className={`text-4xl lg:text-5xl font-semibold tabular-nums tracking-tight transition-colors duration-500 ${flash ? "text-[#1D4D3B]" : "text-[#0A0A0A]"}`}
                          data-testid="live-room-price"
                        >
                          {formatCurrency(
                            lotState === "sold" ? lot.final_price_cents || 0 : hasBids ? lot.current_price_cents : lot.starting_price_cents,
                            currency,
                            locale
                          )}
                        </p>
                        {hasBids && !["sold", "passed"].includes(lotState) && (
                          <p className="text-xs text-[#737373] mt-1" data-testid="live-room-leader">
                            {session.leading_source === "floor"
                              ? t("live.floorLeads")
                              : iLead
                                ? t("live.youLead")
                                : t("bid.paddleLabel", { paddle: lot.leading_paddle ?? "—" })}
                          </p>
                        )}
                      </div>

                      {/* one-tap bid zone */}
                      {["open", "fair_warning"].includes(lotState) && (
                        <div className="flex flex-col items-stretch gap-2 min-w-[220px]" data-testid="live-room-bid-zone">
                          {iLead ? (
                            <div className="rounded-2xl bg-[#1D4D3B] text-white px-6 py-4 text-center font-bold" data-testid="live-room-you-lead">
                              {t("live.youLead")} · #{me.paddle}
                            </div>
                          ) : canBid ? (
                            <Button
                              data-testid="live-room-bid-button"
                              onClick={placeBid}
                              disabled={busy}
                              className={`h-16 rounded-2xl bg-[#0A0A0A] text-white text-lg font-bold hover:bg-black/85 hover:scale-[1.02] active:scale-[0.99] transition-[transform,background-color] duration-200 ${
                                fairWarning ? "ring-2 ring-[#E8A33D] ring-offset-2" : ""
                              }`}
                            >
                              {t("live.bidNow", { amount: formatCurrency(session.current_ask_cents, currency, locale) })}
                            </Button>
                          ) : (
                            <div className="rounded-2xl bg-[#F4F4F3] px-6 py-4 text-center" data-testid="live-room-spectator-box">
                              <p className="text-sm font-semibold text-[#0A0A0A]">
                                {t("live.askLabel", { amount: session.current_ask_cents ? formatCurrency(session.current_ask_cents, currency, locale) : "—" })}
                              </p>
                              {!user ? (
                                <Link to="/signin" data-testid="live-room-signin-cta" className="text-xs font-medium text-[#1D4D3B] hover:underline mt-1 inline-block">
                                  {t("live.signInToBid")}
                                </Link>
                              ) : !me?.registered ? (
                                <button type="button" data-testid="live-room-register-cta" onClick={() => setRegisterOpen(true)} className="text-xs font-medium text-[#1D4D3B] hover:underline mt-1">
                                  {t("live.registerToBid")}
                                </button>
                              ) : (
                                <p className="text-xs text-[#737373] mt-1" data-testid="live-room-pending-note">{t("live.registrationPending")}</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* sold / passed state */}
                    {lotState === "sold" && (
                      <div className={`rounded-2xl px-5 py-4 ${won ? "bg-[#1D4D3B] text-white" : "bg-[#F4F4F3]"}`} data-testid="live-room-sold-banner">
                        <p className={`font-bold ${won ? "text-white" : "text-[#0A0A0A]"}`}>
                          {won
                            ? t("live.youWonBanner", { amount: formatCurrency(lot.final_price_cents || 0, currency, locale) })
                            : t("live.soldBanner", { amount: formatCurrency(lot.final_price_cents || 0, currency, locale) })}
                        </p>
                        {!won && lot.winner_paddle != null && <p className="text-xs text-[#737373] mt-0.5">{t("bid.winnerPaddle", { paddle: lot.winner_paddle })}</p>}
                      </div>
                    )}
                    {lotState === "passed" && (
                      <div className="rounded-2xl bg-[#F4F4F3] px-5 py-4" data-testid="live-room-passed-banner">
                        <p className="font-semibold text-[#737373]">{t("live.passedBanner")}</p>
                      </div>
                    )}
                    {lotState === "upcoming" && (
                      <div className="rounded-2xl bg-[#F9F9F8] px-5 py-4" data-testid="live-room-upcoming-note">
                        <p className="text-sm text-[#737373]">{t("live.lotUpcoming")}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-3xl border border-black/5 px-8 py-12 text-center" data-testid="live-room-between-lots">
                <p className="text-[#737373]">{t("live.betweenLots")}</p>
              </div>
            )}

            {/* up next */}
            {live?.next_lot && (
              <div className="rounded-2xl border border-black/5 px-6 py-4 flex items-center gap-4" data-testid="live-room-up-next">
                {coverSrc(live.next_lot.horse?.cover_url) && (
                  <img src={coverSrc(live.next_lot.horse.cover_url)} alt="" className="w-12 h-12 rounded-xl object-cover" />
                )}
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#737373]">{t("live.upNext")}</p>
                  <p className="text-sm font-semibold text-[#0A0A0A] truncate">
                    {t("pub.lotN", { number: live.next_lot.lot_number })} — {live.next_lot.horse?.name}
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* ---------- ended ---------- */}
        {session?.status === "ended" && (
          <div className="rounded-3xl border border-black/5 bg-[#F9F9F8] px-8 py-12 flex flex-col items-center gap-3 text-center" data-testid="live-room-ended">
            <h2 className="text-2xl font-semibold tracking-tight text-[#0A0A0A]">{t("live.sessionEndedTitle")}</h2>
            <p className="text-sm text-[#737373]">{t("live.sessionEndedDesc", { sold: session.sold_count, total: session.total_lots })}</p>
            <Link to={eventBase(payload?.organizer?.slug, slug)} data-testid="live-room-back-event" className="mt-2 inline-flex items-center h-11 px-6 rounded-full bg-[#0A0A0A] text-white text-sm font-medium hover:bg-black/80 transition-colors duration-300">
              {t("bid.backToEvent")}
            </Link>
          </div>
        )}

        {/* ---------- results so far ---------- */}
        {(live?.results || []).length > 0 && (
          <div className="rounded-3xl border border-black/5 p-6" data-testid="live-room-results">
            <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4">{t("live.resultsSoFar")}</h3>
            <ul className="flex flex-col gap-2.5">
              {live.results
                .slice()
                .reverse()
                .map((r) => (
                  <li key={r.lot_id} className="flex items-center justify-between gap-4 text-sm" data-testid={`live-room-result-${r.lot_number}`}>
                    <span className="text-[#737373] tabular-nums w-14 shrink-0">{t("pub.lotN", { number: r.lot_number })}</span>
                    <span className="flex-1 font-medium text-[#0A0A0A] truncate">{r.horse_name}</span>
                    {r.status === "sold" ? (
                      <span className="font-semibold text-[#1D4D3B] tabular-nums">{r.final_price_cents != null ? formatCurrency(r.final_price_cents, r.currency, locale) : t("bid.sold")}</span>
                    ) : (
                      <span className="text-xs font-semibold uppercase tracking-wider text-[#737373]">{t("bid.notSold")}</span>
                    )}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>

      {dockVisible && (
        <LiveDock
          session={session}
          lot={lot}
          horse={horse}
          me={me}
          iLead={iLead}
          outbid={outbid}
          canBid={canBid}
          busy={busy}
          fairWarning={fairWarning}
          currency={currency}
          locale={locale}
          user={user}
          onBid={placeBid}
          onOpenRegister={() => setRegisterOpen(true)}
        />
      )}

      {payload?.event && (
        <RegisterToBidDialog
          open={registerOpen}
          onOpenChange={setRegisterOpen}
          eventId={payload.event.id}
          eventVisibility={payload.event.visibility || "public"}
          terms={payload.terms}
          requiredLevel={payload.event.verification_level || 1}
          onRegistered={(reg) => {
            setMe((prev) => ({ ...(prev || {}), registered: true, approved: reg.status === "approved", paddle: reg.paddle_number }));
            myPaddleRef.current = reg.paddle_number;
            if (reg.status === "approved") setCelebrate((c) => c + 1);
          }}
        />
      )}
    </div>
  );
}
